import React, { useState } from 'react';
import { generateReviewSummary } from '../utils/openai';

const formatNumber = (num) => {
  return num.toLocaleString();
};

const AISummary = ({ reviews, filteredReviews, currentFilters }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryType, setSummaryType] = useState('filtered');

  const generateSummary = async (type = 'filtered') => {
    const reviewsToSummarize = type === 'all' ? reviews : filteredReviews;
    
    if (reviewsToSummarize.length === 0) {
      setError('No reviews available for summary');
      return;
    }

    setLoading(true);
    setError(null);
    setSummaryType(type);

    try {
      let filterType = 'all';
      if (type === 'filtered' && currentFilters) {
        if (currentFilters.segment) filterType = 'segment';
        else if (currentFilters.rating) filterType = 'rating';
        else if (currentFilters.dateFrom || currentFilters.dateTo) filterType = 'timeframe';
      }

      const newSummary = await generateReviewSummary(reviewsToSummarize, filterType);
      newSummary.reviewCount = reviewsToSummarize.length;
      newSummary.generatedAt = new Date().toLocaleString();
      setSummary(newSummary);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': return '😊';
      case 'negative': return '😟'; 
      case 'mixed': return '😐';
      default: return '🤔';
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive': return '#10b981';
      case 'negative': return '#ef4444';
      case 'mixed': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getFilterDescription = () => {
    if (summaryType === 'all') return 'All Reviews';
    
    const parts = [];
    if (currentFilters?.segment) parts.push(currentFilters.segment);
    if (currentFilters?.rating) parts.push(`${currentFilters.rating} stars`);
    if (currentFilters?.dateFrom || currentFilters?.dateTo) {
      const from = currentFilters.dateFrom || 'start';
      const to = currentFilters.dateTo || 'end';
      parts.push(`${from} to ${to}`);
    }
    if (currentFilters?.searchText) parts.push(`containing "${currentFilters.searchText}"`);
    
    return parts.length > 0 ? parts.join(', ') : 'Filtered Reviews';
  };

  return (
    <div className="ai-summary">
      <div className="summary-header">
        <h3>📝 AI Summary</h3>
        <div className="summary-controls">
          <button 
            className={`summary-type-btn ${summaryType === 'filtered' ? 'active' : ''}`}
            onClick={() => generateSummary('filtered')}
            disabled={loading || filteredReviews.length === 0}
          >
            Current View ({filteredReviews.length})
          </button>
          <button 
            className={`summary-type-btn ${summaryType === 'all' ? 'active' : ''}`}
            onClick={() => generateSummary('all')}
            disabled={loading || reviews.length === 0}
          >
            All Reviews ({formatNumber(reviews.length)})
          </button>
        </div>
      </div>

      {loading && (
        <div className="summary-loading">
          <div className="loading-spinner"></div>
          <p>Generating AI summary...</p>
        </div>
      )}

      {error && (
        <div className="summary-error">
          <p>❌ {error}</p>
          <button onClick={() => generateSummary(summaryType)}>
            Try Again
          </button>
        </div>
      )}

      {!summary && !loading && !error && (
        <div className="summary-prompt">
          <p>Generate an AI-powered summary of your reviews to get key insights and actionable recommendations.</p>
          <button 
            className="generate-summary-btn"
            onClick={() => generateSummary('filtered')}
            disabled={filteredReviews.length === 0}
          >
            Generate Summary
          </button>
        </div>
      )}

      {summary && !loading && (
        <div className="summary-content">
          <div className="summary-meta">
            <div className="summary-scope">
              <strong>Summary of:</strong> {getFilterDescription()}
            </div>
            <div className="summary-stats">
              {summary.reviewCount} reviews • Avg Rating: {summary.averageRating}/5
            </div>
            <div className="summary-timestamp">
              Generated: {summary.generatedAt}
            </div>
          </div>

          <div className="summary-overview">
            <div className="overall-sentiment">
              <span className="sentiment-icon">
                {getSentimentIcon(summary.overallSentiment)}
              </span>
              <span 
                className="sentiment-label"
                style={{ color: getSentimentColor(summary.overallSentiment) }}
              >
                Overall Sentiment: {summary.overallSentiment?.toUpperCase()}
              </span>
            </div>
            
            <div className="executive-summary">
              <h4>Executive Summary</h4>
              <p>{summary.summary}</p>
            </div>
          </div>

          <div className="summary-sections">
            <div className="summary-section">
              <h4>🎯 Key Findings</h4>
              <ul>
                {summary.keyFindings?.map((finding, index) => (
                  <li key={index}>{finding}</li>
                ))}
              </ul>
            </div>

            <div className="summary-section">
              <h4>✨ Strengths</h4>
              <ul>
                {summary.strengths?.map((strength, index) => (
                  <li key={index} className="positive-item">{strength}</li>
                ))}
              </ul>
            </div>

            <div className="summary-section">
              <h4>📈 Areas for Improvement</h4>
              <ul>
                {summary.areasForImprovement?.map((area, index) => (
                  <li key={index} className="improvement-item">{area}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="summary-actions">
            <button 
              className="refresh-summary-btn"
              onClick={() => generateSummary(summaryType)}
              disabled={loading}
            >
              🔄 Refresh Summary
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AISummary;